import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Users, Trash2, Shield, Clock, ShieldCheck, Ban, MoreVertical, Crown, Eye, EyeOff } from 'lucide-react';

export default function AdminUsersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      setTotal(prev => prev - 1);
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleSetRole = async (userId, role) => {
    const roleLabel = role === 'admin' ? 'Admin' : role === 'moderator' ? 'Moderator' : 'User';
    if (!window.confirm(`Set this user's role to ${roleLabel}?`)) return;
    try {
      await api.put(`/admin/users/${userId}/role`, { role });
      setUsers(users.map(u => u.id === userId ? { ...u, role } : u));
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleBlock = async (userId) => {
    try {
      const res = await api.put(`/admin/users/${userId}/block`);
      setUsers(users.map(u => u.id === userId ? { ...u, blocked: res.data.blocked } : u));
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleToggleProfile = async (userId) => {
    try {
      const res = await api.put(`/admin/users/${userId}/profile-disabled`);
      setUsers(users.map(u => u.id === userId ? { ...u, profile_disabled: res.data.profile_disabled } : u));
    } catch (err) { alert(formatApiError(err)); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown';

  const getRoleBadge = (role) => {
    if (role === 'admin') return <span className="tag bg-[#FF3B30] text-white px-2 py-0.5 rounded">ADMIN</span>;
    if (role === 'moderator') return <span className="tag bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">MOD</span>;
    return <span className="tag bg-[#262626] text-gray-400 px-2 py-0.5 rounded">USER</span>;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-8">
          <p className="tag text-[#FF3B30] mb-2">Admin</p>
          <h1 className="font-heading font-bold text-3xl text-white mb-2">Registered Users</h1>
          <p className="text-gray-400 text-sm">{total} total users</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Desktop Table */}
        <div className="hidden md:block bg-[#141414] border border-[#262626] rounded overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#262626] text-left">
                <th className="px-6 py-4 text-xs uppercase tracking-wider text-gray-500 font-semibold">User</th>
                <th className="px-6 py-4 text-xs uppercase tracking-wider text-gray-500 font-semibold">Email</th>
                <th className="px-6 py-4 text-xs uppercase tracking-wider text-gray-500 font-semibold">Role</th>
                <th className="px-6 py-4 text-xs uppercase tracking-wider text-gray-500 font-semibold">Status</th>
                <th className="px-6 py-4 text-xs uppercase tracking-wider text-gray-500 font-semibold">Joined</th>
                <th className="px-6 py-4 text-xs uppercase tracking-wider text-gray-500 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#262626]">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-[#1F1F1F] transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm ${u.blocked ? 'bg-red-900' : 'bg-[#262626]'}`}>
                        {u.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <span className="text-white font-medium">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-400 text-sm">{u.email}</td>
                  <td className="px-6 py-4">{getRoleBadge(u.role)}</td>
                  <td className="px-6 py-4">
                    {u.blocked ? (
                      <span className="tag bg-red-500/20 text-red-400 px-2 py-0.5 rounded">BLOCKED</span>
                    ) : (
                      <span className="tag bg-green-500/20 text-green-400 px-2 py-0.5 rounded">ACTIVE</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-500 text-sm">{formatDate(u.created_at)}</td>
                  <td className="px-6 py-4 text-right">
                    {u.id !== user.id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white" data-testid={`user-actions-${u.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626] w-48">
                          <DropdownMenuItem asChild className="cursor-pointer">
                            <Link to={`/profile/${u.id}`} target="_blank" rel="noreferrer">
                              <Eye className="w-4 h-4 mr-2 text-gray-300" /> View Profile
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleProfile(u.id)} className="cursor-pointer">
                            <EyeOff className="w-4 h-4 mr-2 text-yellow-400" />
                            {u.profile_disabled ? 'Enable Profile' : 'Disable Profile'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-[#262626]" />
                          <DropdownMenuItem onClick={() => handleSetRole(u.id, 'admin')} className="cursor-pointer" disabled={u.role === 'admin'}>
                            <Crown className="w-4 h-4 mr-2 text-[#FF3B30]" /> Make Admin
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleSetRole(u.id, 'moderator')} className="cursor-pointer" disabled={u.role === 'moderator'}>
                            <ShieldCheck className="w-4 h-4 mr-2 text-blue-400" /> Make Moderator
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleSetRole(u.id, 'user')} className="cursor-pointer" disabled={u.role === 'user'}>
                            <Shield className="w-4 h-4 mr-2" /> Set as User
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-[#262626]" />
                          <DropdownMenuItem onClick={() => handleBlock(u.id)} className="cursor-pointer">
                            <Ban className={`w-4 h-4 mr-2 ${u.blocked ? 'text-green-400' : 'text-yellow-400'}`} />
                            {u.blocked ? 'Unblock User' : 'Block User'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-[#262626]" />
                          <DropdownMenuItem onClick={() => handleDelete(u.id, u.name)} className="cursor-pointer text-red-400">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {users.map((u) => (
            <Card key={u.id} className="bg-[#141414] border-[#262626] p-4" data-testid={`user-card-${u.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${u.blocked ? 'bg-red-900' : 'bg-[#262626]'}`}>
                    {u.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="text-white font-medium">{u.name}</p>
                    <p className="text-gray-500 text-xs">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getRoleBadge(u.role)}
                  {u.blocked && <span className="tag bg-red-500/20 text-red-400 px-2 py-0.5 rounded">BLOCKED</span>}
                </div>
              </div>
              {u.id !== user.id && (
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#262626]">
                  <span className="text-gray-500 text-xs flex items-center gap-1"><Clock className="w-3 h-3" />Joined {formatDate(u.created_at)}</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-gray-400"><MoreVertical className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626] w-48">
                      <DropdownMenuItem asChild className="cursor-pointer"><Link to={`/profile/${u.id}`} target="_blank" rel="noreferrer"><Eye className="w-4 h-4 mr-2 text-gray-300" /> View Profile</Link></DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleProfile(u.id)} className="cursor-pointer"><EyeOff className="w-4 h-4 mr-2 text-yellow-400" />{u.profile_disabled ? 'Enable Profile' : 'Disable Profile'}</DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#262626]" />
                      <DropdownMenuItem onClick={() => handleSetRole(u.id, 'admin')} className="cursor-pointer"><Crown className="w-4 h-4 mr-2 text-[#FF3B30]" /> Make Admin</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSetRole(u.id, 'moderator')} className="cursor-pointer"><ShieldCheck className="w-4 h-4 mr-2 text-blue-400" /> Make Moderator</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSetRole(u.id, 'user')} className="cursor-pointer"><Shield className="w-4 h-4 mr-2" /> Set as User</DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#262626]" />
                      <DropdownMenuItem onClick={() => handleBlock(u.id)} className="cursor-pointer"><Ban className={`w-4 h-4 mr-2 ${u.blocked ? 'text-green-400' : 'text-yellow-400'}`} />{u.blocked ? 'Unblock' : 'Block'}</DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#262626]" />
                      <DropdownMenuItem onClick={() => handleDelete(u.id, u.name)} className="cursor-pointer text-red-400"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </Card>
          ))}
        </div>

        {users.length === 0 && (
          <div className="text-center py-16">
            <Users className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 text-lg">No registered users.</p>
          </div>
        )}
      </div>
    </div>
  );
}
